import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Gem, Search, Heart, CheckCircle } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import CitySelector from '../components/CitySelector';
import RotatingText from '../components/RotatingText';
import Card from '../components/Card';
import TextPlaceholder from '../components/TextPlaceholder';
import useEscortsData from '../hooks/useEscortsData';
import { Escort } from '../types';
import { createEscortUrl } from '../utils/urlUtils';
import { useCountryCode } from '../hooks/useCountryCode';
import { useCountryContext } from '../contexts/CountryContext';
import { loadMostPopularConfig } from '../utils/mostPopularConfig';
import { useTranslation } from '../hooks/useTranslation';
import { useSearch } from '../contexts/SearchContext';
import useMobile from '../hooks/useMobile';
import { useDisclaimer } from '../contexts/DisclaimerContext';
import ShowMoreButton from '../components/atoms/ShowMoreButton';
import SectionHeader from '../components/atoms/SectionHeader';
import { IMG_DOMAIN } from '../config/domain';
import { faqStructuredData } from '../data/seoMetaTags';

const HOME_PAGE_MODELS_STATUS_KEY = 'models_status_cache';
const HOME_PAGE_MODELS_STATUS_TTL = 24 * 60 * 60 * 1000; // 1 giorno

type ModelStatus = {
  hasGallery?: boolean;
  hasReviews?: boolean;
  hasSelfie?: boolean;
  hasVideo?: boolean;
};

type ModelsStatusCache = {
  timestamp: number;
  data: Record<string, ModelStatus>;
};

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['1', 'true', 'yes', 'on'].includes(normalized);
  }
  return false;
};

const getStatusValue = (status: any, ...keys: string[]): boolean => {
  for (const key of keys) {
    if (key in status) return normalizeBoolean(status[key]);
  }
  return false;
};

const getLookupKeys = (baseKey: string) => {
  switch (baseKey) {
    case 'gallery':
      return ['gallery', 'hasGallery', 'has_gallery'];
    case 'video':
      return ['video', 'hasVideo', 'has_video'];
    case 'reviews':
      return ['recensioni', 'reviews', 'hasReviews', 'has_reviews'];
    case 'selfie':
      return ['selfie', 'hasSelfie', 'has_selfie'];
    default:
      return [baseKey];
  }
};

const buildStatusMap = (raw: unknown): Record<string, ModelStatus> => {
  if (!raw || typeof raw !== 'object') return {};

  const statusMap: Record<string, ModelStatus> = {};

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const id = String((item as any).id || (item as any).ID || (item as any).modelId || (item as any).model_id);
      if (!id) return;
      statusMap[id] = {
        hasGallery: getStatusValue(item, ...getLookupKeys('gallery')),
        hasReviews: getStatusValue(item, ...getLookupKeys('reviews')),
        hasSelfie: getStatusValue(item, ...getLookupKeys('selfie')),
        hasVideo: getStatusValue(item, ...getLookupKeys('video')),
      };
    });
  } else {
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      if (!value || typeof value !== 'object') return;
      statusMap[key] = {
        hasGallery: getStatusValue(value, ...getLookupKeys('gallery')),
        hasReviews: getStatusValue(value, ...getLookupKeys('reviews')),
        hasSelfie: getStatusValue(value, ...getLookupKeys('selfie')),
        hasVideo: getStatusValue(value, ...getLookupKeys('video')),
      };
    });
  }

  return statusMap;
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { countryCode } = useCountryCode();
  const { selectedCountry } = useCountryContext();
  const { t } = useTranslation();
  const { isMobileSearchVisible } = useSearch();
  const isMobile = useMobile();
  const { hasAcceptedDisclaimer } = useDisclaimer();
  const { escorts } = useEscortsData();

  const filteredEscorts = React.useMemo(() => {
    if (!escorts || !countryCode) return escorts || [];
    return escorts.filter(escort => !escort.blocked?.includes(countryCode));
  }, [escorts, countryCode]);

  const handleCitySelect = (location: string) => {
    navigate(`/location/${encodeURIComponent(location)}`);
  };

  const [popularConfig, setPopularConfig] = React.useState<{ mostReviewd?: string; mostVisited?: string; mostContacted?: string; mostReviewed?: string; } | null>(null);
  const [modelsStatus, setModelsStatus] = React.useState<Record<string, ModelStatus>>({});

  React.useEffect(() => {
    // Load most-popular config using shared helper (based on selectedCountry or countryCode)
    let cancelled = false;
    (async () => {
      try {
        const block = await loadMostPopularConfig(selectedCountry, countryCode);
        if (!cancelled && block) setPopularConfig(block);
      } catch (e) {
        // ignore
      }
    })();

    let cacheValid = false;
    const cached = localStorage.getItem(HOME_PAGE_MODELS_STATUS_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as ModelsStatusCache;
        if (parsed?.timestamp && parsed?.data && (Date.now() - parsed.timestamp) < HOME_PAGE_MODELS_STATUS_TTL) {
          setModelsStatus(parsed.data);
          cacheValid = true;
        }
      } catch {
        localStorage.removeItem(HOME_PAGE_MODELS_STATUS_KEY);
      }
    }

    if (!cacheValid) {
      fetch(`${IMG_DOMAIN}/cache/models_status.php`)
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          const map = buildStatusMap(data);
          if (Object.keys(map).length > 0) {
            setModelsStatus(map);
            localStorage.setItem(HOME_PAGE_MODELS_STATUS_KEY, JSON.stringify({
              timestamp: Date.now(),
              data: map,
            }));
          }
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
    // Re-run when selectedCountry or countryCode change so the correct block is applied
  }, [selectedCountry, countryCode]);

  const getCardFlags = (escort: Escort) => {
    // Prefer API-provided international phone (`pphone`) for external lookups
    // because `phone` may be a local number without international prefix.
    const lookupKey = escort.pphone?.trim() || escort.phone?.trim() || escort.whatsappNumber?.trim() || escort.id;
    const status = lookupKey ? (modelsStatus[lookupKey] || {}) : {};
    return {
      mostVisited: popularConfig ? escort.id === popularConfig.mostVisited : false,
      mostContacted: popularConfig ? escort.id === popularConfig.mostContacted : false,
      mostReviewed: popularConfig ? escort.id === (popularConfig.mostReviewd || popularConfig.mostReviewed) : false,
      hasGallery: !!status.hasGallery,
      hasReviews: !!status.hasReviews,
      hasSelfie: !!status.hasSelfie,
      hasVideo: !!status.hasVideo,
    };
  };

  const handleSearch = (query: string, location: string) => {
    navigate(`/escorts?search=${query}&location=${location}`);
  };

  // FAQ JSON-LD
  React.useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqStructuredData);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);
  
  const handleViewProfile = (id: string) => {
    const escort = filteredEscorts.find(e => e.id === id);
    if (escort && escort.phone) {
      const url = createEscortUrl({
        phone: escort.phone,
        name: escort.name,
        location: escort.location
      });
      navigate(url);
    }
  };
  
  // Funzione per randomizzare array
  function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Stato per mostrare/nascondere tutte le righe
  const [showAll, setShowAll] = React.useState(false);
  const [showAllRegular, setShowAllRegular] = React.useState(false);
  const [showAllVip, setShowAllVip] = React.useState(false);

  const vipRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => e.featured));
  }, [filteredEscorts]);

  const vetrinaRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => !e.featured && e.isVetrina));
  }, [filteredEscorts]);
  
  const regularRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => !e.featured && !e.isVetrina));
  }, [filteredEscorts]);

  const rotatingWords = [
    t('common.categories.compagna'),
    t('common.categories.escort'),
    t('common.trans'),
    // t('common.categories.accompagnatrice'),
    t('common.categories.mistress'),
  ];

  return (
    <div>
      {hasAcceptedDisclaimer && (
      <section className={`relative bg-gradient-to-r from-pink-500 to-purple-600 text-white transition-all duration-300 ${isMobile && !isMobileSearchVisible ? 'hidden' : ''}`}>
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="relative container mx-auto px-2 py-8 md:py-24 flex flex-col items-center text-center">

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ minHeight: '4rem' }}>
              <TextPlaceholder 
                translationKey="homePage.heroTitle"
                className="text-4xl md:text-5xl lg:text-6xl font-bold"
                minHeight="4rem"
                fallbackText="Trova la tua"
              /> <span style={{ display: 'inline-block', minWidth: '200px', textAlign: 'center' }}>
                <RotatingText words={rotatingWords} className="text-white" />
              </span> <TextPlaceholder 
                translationKey="common.rotating.perfetta"
                className="text-4xl md:text-5xl lg:text-6xl font-bold"
                fallbackText="perfetta"
              />
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-4xl" style={{ minHeight: '3rem' }}>
              <TextPlaceholder 
                translationKey="homePage.heroSubtitle"
                className="text-xl md:text-2xl"
                minHeight="3rem"
                fallbackText="Scopri migliaia di"
              /> <RotatingText words={rotatingWords} className="font-medium" /> <TextPlaceholder 
                translationKey="common.rotating.paese"
                className="text-xl md:text-2xl"
                fallbackText="in tutto il paese"
              />
            </p>
            
            <div className="w-full max-w-4xl">
              <SearchBar onSearch={handleSearch} />
            </div>

        </div>
      </section>
      )}

      <CitySelector onCitySelect={handleCitySelect} />

      {/* Sezione Annunci VIP */}
      {vipRandom.length > 0 && hasAcceptedDisclaimer && (
        <section 
          className="py-8 bg-gradient-to-r from-fuchsia-50 to-pink-50"
        >
          <div className="container mx-auto px-2">
            <div className="hidden md:flex items-center justify-between mb-8">

              <SectionHeader
                icon={<Crown className="h-8 w-8 text-fuchsia-500" />}
                title={
                  <TextPlaceholder
                    translationKey="homePage.vipAdsTitle"
                    className="text-3xl font-bold text-gray-800"
                    minHeight="2.5rem"
                    fallbackText="Annunci VIP"
                  />
                }
                titleStyle={{ minHeight: '2.5rem', minWidth: '12ch' }}
              />

            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {(showAllVip ? vipRandom : vipRandom.slice(0, 8)).map((escort, index) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                  priority={index === 0}
                  source="home"
                  {...getCardFlags(escort)}
                />
              ))}
            </div>
            {vipRandom.length > 8 && (
              <ShowMoreButton
                onClick={() => setShowAllVip((prev) => !prev)}
                colorClass="bg-fuchsia-500 hover:bg-fuchsia-600"
              >
                <TextPlaceholder
                  translationKey={showAllVip ? "homePage.hideButton" : "homePage.showAllButton"}
                  minHeight="1.5rem"
                  fallbackText={showAllVip ? "Nascondi" : "Mostra tutto"}
                />
              </ShowMoreButton>
            )}
          </div>
        </section>
      )}
      
      {/* Sezione Annunci Vetrina */}
      {vetrinaRandom.length > 0 && hasAcceptedDisclaimer && (
        <section 
          className="py-8 bg-gradient-to-r from-yellow-50 to-orange-50"
        >
          <div className="container mx-auto px-2">
            <div className="hidden md:flex items-center justify-between mb-8">
              <SectionHeader
                icon={<Gem className="h-8 w-8 text-yellow-500" />}
                title={t('homePage.showcaseAdsTitle')}
              />
              <p className="text-gray-600 hidden">{t('homePage.showcaseAdsCount', { count: vetrinaRandom.length })}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {(showAll ? vetrinaRandom : vetrinaRandom.slice(0, 8)).map((escort) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                  source="home"
                  {...getCardFlags(escort)}
                />
              ))}
            </div>
            {vetrinaRandom.length > 8 && (
              <ShowMoreButton
                onClick={() => setShowAll((prev) => !prev)}
                colorClass="bg-yellow-400 hover:bg-yellow-500"
              >
                {showAll ? t('homePage.hideButton') : t('homePage.showAllButton')}
              </ShowMoreButton>
            )}
          </div>
        </section>
      )}

      {/* Sezione Annunci Normali */}
      {regularRandom.length > 0 && hasAcceptedDisclaimer && (
        <section 
          className="py-8"
        >
          <div className="container mx-auto px-2">
            <div className="hidden md:flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-800" style={{ minHeight: '2.5rem', minWidth: '12ch' }}>
                <TextPlaceholder 
                  translationKey="homePage.regularAdsTitle"
                  className="text-3xl font-bold text-gray-800"
                  minHeight="2.5rem"
                  fallbackText="Annunci"
                />
              </h2>
              <p className="text-gray-600 hidden" style={{ minHeight: '1.5rem', minWidth: '8ch' }}>
                <TextPlaceholder 
                  translationKey="homePage.regularAdsCount"
                  replacements={{ count: regularRandom.length }}
                  minHeight="1.5rem"
                  fallbackText={`${regularRandom.length} annunci`}
                />
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {(showAllRegular ? regularRandom : regularRandom.slice(0, 8)).map((escort) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                  source="home"
                  {...getCardFlags(escort)}
                />
              ))}
            </div>

            {regularRandom.length > 8 && (
              <ShowMoreButton
                onClick={() => setShowAllRegular((prev) => !prev)}
              >
                <TextPlaceholder
                  translationKey={showAllRegular ? "homePage.hideButton" : "homePage.showAllButton"}
                  minHeight="1.5rem"
                  fallbackText={showAllRegular ? "Nascondi" : "Mostra tutto"}
                />
              </ShowMoreButton>
            )}
          </div>
        </section>
      )}
      
      {hasAcceptedDisclaimer &&
      <section className="py-8">
        <div className="container mx-auto px-2">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12" style={{ minHeight: '2.5rem' }}>
            <TextPlaceholder 
              translationKey="homePage.howItWorksTitle"
              className="text-3xl font-bold text-gray-800"
              minHeight="2.5rem"
            />
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.searchStepTitle"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.searchStepDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.contactStepTitle"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.contactStepDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.enjoyStepTitle"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem' }}>
                <TextPlaceholder 
                  translationKey="homePage.enjoyStepDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>
          </div>
        </div>
      </section>
      }

      {hasAcceptedDisclaimer && 
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-2">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12" style={{ minHeight: '2.5rem' }}>
            <TextPlaceholder 
              translationKey="common.perch_scegliere_escortfun"
              className="text-3xl font-bold text-gray-800"
              minHeight="2.5rem"
            />
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem' }}>
                <TextPlaceholder 
                  translationKey="common.profili_verificati"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem !important' }}>
                <TextPlaceholder 
                  translationKey="homePage.verifiedProfilesDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem !important' }}>
                <TextPlaceholder 
                  translationKey="homePage.privateSecureTitle"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem !important' }}>
                <TextPlaceholder 
                  translationKey="homePage.privateSecureDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3" style={{ minHeight: '1.75rem !important' }}>
                <TextPlaceholder 
                  translationKey="homePage.wideSelectionTitle"
                  className="text-xl font-semibold"
                  minHeight="1.75rem"
                />
              </h3>
              <p className="text-gray-600" style={{ minHeight: '5rem !important' }}>
                <TextPlaceholder 
                  translationKey="homePage.wideSelectionDescription"
                  className="text-gray-600"
                  minHeight="5rem"
                />
              </p>
            </div>
          </div>
        </div>
      </section>
      }

    </div>
  );
};

export default HomePage;

